// src/services/ttn/readings.js
// Sensor readings from the AfriClimate data API

import { AFRICLIMATE_API as TTN, apiHeaders as ttnHeaders, handleResponse } from '../config';

/** Map TTN sensorData → app-standard field names */
export const mapReading = (r) => ({
  id:         r._id || r.uniqueId,
  deviceId:   r.deviceId,
  timestamp:  r.receivedAt,
  // Weather fields (renamed to match app conventions)
  temperature: r.sensorData?.airTemperature     ?? null,
  humidity:    r.sensorData?.airHumidity         ?? null,
  pressure:    r.sensorData?.barometricPressure != null
                 ? r.sensorData.barometricPressure / 100   // Pa → hPa
                 : null,
  wind_speed:  r.sensorData?.windSpeed           ?? null,
  wind_dir:    r.sensorData?.windDirectionSensor ?? null,
  peak_gust:   r.sensorData?.peakWindGust        ?? null,
  rain_gauge:  r.sensorData?.rainGauge           ?? null,
  rain_accum:  r.sensorData?.rainAccumulation    ?? null,
  uv_index:    r.sensorData?.uvIndex             ?? null,
  light:       r.sensorData?.lightIntensity      ?? null,
  // LoRaWAN signal info (best gateway = strongest RSSI)
  rssi:        r.gateway?.rssi       ?? null,
  snr:         r.gateway?.snr        ?? null,
  gatewayId:   r.gateway?.gatewayId  ?? null,
  // All gateways that received this packet (sorted strongest RSSI first by API)
  allGateways: Array.isArray(r.allGateways)
    ? r.allGateways.map(g => ({ gatewayId: g.gatewayId, rssi: g.rssi, snr: g.snr }))
    : (r.gateway?.gatewayId ? [{ gatewayId: r.gateway.gatewayId, rssi: r.gateway.rssi, snr: r.gateway.snr }] : []),
  // Analytics
  linkQuality: r.analytics?.linkQuality ?? null,
  distance_km: r.analytics?.distance   ?? null,
  linkBudget:  r.analytics?.linkBudget  ?? null,
});

/** Latest reading per device */
export const fetchLatestReadings = async (applicationId = null) => {
  const url = applicationId
    ? `${TTN.BASE_URL}/readings/latest?applicationId=${applicationId}`
    : `${TTN.BASE_URL}/readings/latest`;
  const json = await handleResponse(await fetch(url, { headers: ttnHeaders }));
  return (json.data || []).map(mapReading);
};

/** Paginated readings with optional filters */
export const fetchReadings = async ({
  deviceId      = null,
  applicationId = null,
  startDate     = null,
  endDate       = null,
  page          = 1,
  limit         = 50,
} = {}) => {
  const p = new URLSearchParams({ page, limit });
  if (deviceId)      p.set('deviceId', deviceId);
  if (applicationId) p.set('applicationId', applicationId);
  if (startDate)     p.set('startDate', startDate);
  if (endDate)       p.set('endDate', endDate);
  const json = await handleResponse(
    await fetch(`${TTN.BASE_URL}/readings?${p}`, { headers: ttnHeaders })
  );
  return { data: (json.data || []).map(mapReading), total: json.total ?? null };
};

/**
 * Aggregated readings for charts
 * interval: 'hour' | 'day' | 'week' | 'month'
 */
export const fetchAggregatedReadings = async ({
  deviceId,
  interval  = 'hour',
  startDate = null,
  endDate   = null,
} = {}) => {
  const p = new URLSearchParams({ deviceId, interval });
  if (startDate) p.set('startDate', startDate);
  if (endDate)   p.set('endDate', endDate);
  const json = await handleResponse(
    await fetch(`${TTN.BASE_URL}/readings/aggregated?${p}`, { headers: ttnHeaders })
  );
  return json.data || [];
};

/** Device statistics (avg/min/max temp, total rainfall, etc.) */
export const fetchDeviceStats = async (deviceId, startDate = null) => {
  const p = new URLSearchParams();
  if (startDate) p.set('startDate', startDate);
  const json = await handleResponse(
    await fetch(`${TTN.BASE_URL}/readings/device/${deviceId}/stats?${p}`, { headers: ttnHeaders })
  );
  return json.data || null;
};
